import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Grid,
  Chip,
  Avatar,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CardMedia,
  Divider
} from '@mui/material';
import {
  Person,
  WhatsApp,
  Schedule,
  Close
} from '@mui/icons-material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { eventParticipationService } from '../../services/eventParticipationService';
import { formatPhoneOnInput } from '../../utils/phoneFormatter';

const EventParticipationModal = ({ open, onClose, event }) => {
  const [formData, setFormData] = useState({
    dj_name: '',
    whatsapp: '',
    time_slot: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Buscar horários disponíveis
  const { data: timeSlotsData, isLoading: timeSlotsLoading } = useQuery({
    queryKey: ['eventTimeSlots', event?.id],
    queryFn: () => eventParticipationService.getAvailableTimeSlots(event.id),
    enabled: !!event?.id && open
  });

  const timeSlots = timeSlotsData?.data || { available: [], occupied: [], participations: [] };

  // Mutation para criar participação
  const createParticipationMutation = useMutation({
    mutationFn: (data) => eventParticipationService.createParticipation(event.id, data),
    onSuccess: () => {
      setSnackbar({ open: true, message: 'Solicitação enviada! Verifique seu email (e a caixa de spam).', severity: 'success' });
      handleClose();
    },
    onError: (error) => {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Erro ao enviar solicitação', severity: 'error' });
    }
  });

  const handleClose = () => {
    setFormData({
      dj_name: '',
      whatsapp: '',
      time_slot: ''
    });
    setSelectedFile(null);
    setPreviewUrl(null);
    onClose();
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const handleSubmit = () => {
    if (!formData.dj_name || !formData.whatsapp || !formData.time_slot) {
      setSnackbar({ open: true, message: 'Preencha todos os campos obrigatórios', severity: 'error' });
      return;
    }

    // Create FormData to include file upload
    const submitData = new FormData();
    submitData.append('dj_name', formData.dj_name);
    submitData.append('whatsapp', formData.whatsapp);
    submitData.append('time_slot', formData.time_slot);
    
    // Add profile image if selected
    if (selectedFile) {
      submitData.append('profile_image', selectedFile);
    }

    createParticipationMutation.mutate(submitData);
  };

  const formatTime = (time) => {
    return time.substring(0, 5);
  };

  if (!event) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ color: '#06B6D4', fontWeight: 700, textShadow: '0 0 10px rgba(6, 182, 212, 0.4)' }}>
            Participar do Evento
          </Typography>
          <Button onClick={handleClose} sx={{ color: '#06B6D4', '&:hover': { color: '#22D3EE' } }}>
            <Close />
          </Button>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {/* Informações do Evento */}
          <Card sx={{ mb: 3, bgcolor: 'rgba(6, 182, 212, 0.08)', border: '2px solid rgba(6, 182, 212, 0.3)', borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: '#06B6D4', mb: 1, fontWeight: 600, textShadow: '0 0 5px rgba(6, 182, 212, 0.3)' }}>
                {event.title}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 2 }}>
                {event.description}
              </Typography>
              <Box display="flex" alignItems="center" gap={2}>
                <Schedule sx={{ color: '#06B6D4', fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: '#06B6D4', fontWeight: 500 }}>
                  {new Date(event.event_date).toLocaleDateString('pt-BR')} - {formatTime(event.start_time)} às {formatTime(event.end_time)}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          <Grid container spacing={3}>
            {/* Formulário de Participação */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ color: '#06B6D4', mb: 2, fontWeight: 600, textShadow: '0 0 5px rgba(6, 182, 212, 0.3)' }}>
                Seus Dados
              </Typography>
              
              <TextField
                fullWidth
                label="Nome do DJ *"
                value={formData.dj_name}
                onChange={(e) => setFormData({ ...formData, dj_name: e.target.value })}
                margin="normal"
                InputProps={{
                  startAdornment: <Person sx={{ mr: 1, color: '#06B6D4' }} />
                }}
              />
              
              <TextField
                fullWidth
                label="WhatsApp *"
                value={formData.whatsapp}
                onChange={(e) => setFormData({ ...formData, whatsapp: formatPhoneOnInput(e.target.value) })}
                margin="normal"
                placeholder="(11) 99999-9999"
                InputProps={{
                  startAdornment: <WhatsApp sx={{ mr: 1, color: '#25D366' }} />
                }}
              />
              
              {/* Campos de redes sociais removidos - opcional no dashboard */}

              {/* Upload de Foto */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 1, color: '#06B6D4', fontWeight: 500 }}>
                  Foto do DJ (opcional)
                </Typography>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="dj-photo-upload"
                  type="file"
                  onChange={handleFileSelect}
                />
                <label htmlFor="dj-photo-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    sx={{
                      borderColor: '#06B6D4',
                      color: '#06B6D4',
                      '&:hover': {
                        borderColor: '#22D3EE',
                        bgcolor: 'rgba(6, 182, 212, 0.08)'
                      }
                    }}
                  >
                    Selecionar Foto
                  </Button>
                </label>
                {previewUrl && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Avatar
                      src={previewUrl}
                      sx={{ width: 80, height: 80, mx: 'auto' }}
                    />
                  </Box>
                )}
              </Box>
            </Grid>

            {/* Seleção de Horário */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ color: '#06B6D4', mb: 2, fontWeight: 600, textShadow: '0 0 5px rgba(6, 182, 212, 0.3)' }}>
                Escolha seu Horário
              </Typography>

              {timeSlotsLoading ? (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress sx={{ color: '#06B6D4' }} />
                </Box>
              ) : (
                <Box>
                  {/* Horários Disponíveis */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ color: '#10B981', mb: 1, fontWeight: 600 }}>
                      Horários Disponíveis
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {timeSlots.available.map((slot) => (
                        <Chip
                          key={slot}
                          label={formatTime(slot)}
                          onClick={() => setFormData({ ...formData, time_slot: slot })}
                          sx={{
                            bgcolor: formData.time_slot === slot ? '#10B981' : 'rgba(16, 185, 129, 0.1)',
                            color: formData.time_slot === slot ? 'white' : '#10B981',
                            cursor: 'pointer',
                            '&:hover': {
                              bgcolor: formData.time_slot === slot ? '#10B981' : 'rgba(16, 185, 129, 0.2)'
                            }
                          }}
                        />
                      ))}
                    </Box>
                    {timeSlots.available.length === 0 && (
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontStyle: 'italic' }}>
                        Nenhum horário disponível
                      </Typography>
                    )}
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Horários Ocupados */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: '#F59E0B', mb: 1, fontWeight: 600 }}>
                      Horários Ocupados
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {timeSlots.occupied.map((slot) => (
                        <Chip
                          key={slot}
                          label={formatTime(slot)}
                          disabled
                          sx={{
                            bgcolor: 'rgba(245, 158, 11, 0.1)',
                            color: '#F59E0B'
                          }}
                        />
                      ))}
                    </Box>
                    {timeSlots.occupied.length === 0 && (
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontStyle: 'italic' }}>
                        Nenhum horário ocupado
                      </Typography>
                    )}
                  </Box>

                  {/* Participações Confirmadas */}
                  {timeSlots.participations.length > 0 && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Box>
                        <Typography variant="subtitle2" sx={{ color: '#06B6D4', mb: 1, fontWeight: 600 }}>
                          Participações Confirmadas
                        </Typography>
                        <Box sx={{ maxHeight: 150, overflow: 'auto' }}>
                          {timeSlots.participations.map((participation) => (
                            <Box key={participation.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Avatar
                                src={participation.profile_image}
                                sx={{ width: 30, height: 30 }}
                              >
                                {participation.dj_name?.charAt(0)}
                              </Avatar>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" sx={{ color: 'white' }}>
                                  {participation.dj_name}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#06B6D4', fontWeight: 500 }}>
                                  {formatTime(participation.time_slot)}
                                </Typography>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </>
                  )}
                </Box>
              )}
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button 
          onClick={handleClose}
          sx={{
            color: '#F59E0B',
            borderColor: '#F59E0B',
            '&:hover': {
              color: '#FBBF24',
              borderColor: '#FBBF24',
              bgcolor: 'rgba(245, 158, 11, 0.08)'
            }
          }}
          variant="outlined"
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={createParticipationMutation.isPending || !formData.dj_name || !formData.whatsapp || !formData.time_slot}
          sx={{ 
            bgcolor: '#06B6D4', 
            '&:hover': { bgcolor: '#22D3EE' },
            '&:disabled': { bgcolor: 'rgba(6, 182, 212, 0.3)' }
          }}
        >
          {createParticipationMutation.isPending ? <CircularProgress size={20} /> : 'Enviar Solicitação'}
        </Button>
      </DialogActions>

      {/* Snackbar para feedback */}
      {snackbar.open && (
        <Alert
          severity={snackbar.severity}
          sx={{ position: 'fixed', top: 20, right: 20, zIndex: 9999 }}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      )}
    </Dialog>
  );
};

export default EventParticipationModal;
